import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import dayjs from 'dayjs';
import equal from 'fast-deep-equal';
import { _, TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Button } from 'primeng/button';
import { Select } from 'primeng/select';
import { Textarea } from 'primeng/textarea';
import { RadioButton } from 'primeng/radiobutton';
import { SvgIconComponent } from 'angular-svg-icon';
import { toDateStr } from '@app/shared/utils/date';
import { Pressure } from '@app/shared/units/pressure/pressure.model';
import { PressureUnit } from '@app/shared/units/pressure/pressure-unit.enum';
import { DEFAULT_PRESSURE_UNIT } from '@app/shared/units/pressure/pressure-unit.localize';
import { positiveNumber } from '@app/shared/validators/positive-number.validator';
import { trimmedMinLength } from '@app/shared/validators/trimmed-min-length.validator';
import { dialogEventRepairDateValidator } from '@app/main/leak-detection/dialogs/dialog-event/validators/dialog-event-repair-date.validator';
import { LdEventRepairStatus } from '@app/main/leak-detection/enums/ld-event-repair-status.enum';
import { NonLeakNoiseSource } from '@app/core/enums/non-leak-noise-source.enum';
import { LdEventInvestigationResult } from '@app/main/leak-detection/enums/ld-event-investigation-result.enum';
import { isLeakFoundResult } from '@app/main/leak-detection/utils/ld-event-status';
import { LeakDetectionEventsAssigneeService } from '@app/main/leak-detection/services/leak-detection-events-assignee/leak-detection-events-assignee.service';
import {
  DialogEventCommentRequirement,
  DialogEventStatusActionForm,
} from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-status-action.enum';
import { DialogEventEditor } from '@app/main/leak-detection/dialogs/dialog-event/enums/dialog-event-editor.enum';
import {
  dialogEventStatusTransitions,
  IDialogEventStatusTransition,
} from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-status-transitions.config';
import { dialogEventResultLabel } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-badge.config';
import { DialogEventBadgesComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-badges/dialog-event-badges.component';
import { DialogEventResolutionFieldsComponent } from '@app/main/leak-detection/dialogs/dialog-event/components/dialog-event-resolution-fields/dialog-event-resolution-fields.component';
import { DialogEventState } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-state.service';
import { DialogEventTabsService } from '@app/main/leak-detection/dialogs/dialog-event/services/dialog-event-tabs.service';
import { IDialogEventDraft } from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-draft.interface';
import {
  needsAssignee,
  pressureDisplayValue,
} from '@app/main/leak-detection/dialogs/dialog-event/utils/dialog-event-draft';
import {
  DialogEventResolutionForm,
  IDialogEventChangeForm,
  IDialogEventResolutionForm,
} from '@app/main/leak-detection/dialogs/dialog-event/interfaces/dialog-event-resolution-form.interface';
import { DIALOG_EVENT_OVERLAY_OPTIONS } from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-overlay.config';
import {
  DIALOG_EVENT_COMMENT_WORDING,
  DIALOG_EVENT_MAX_COMMENT_LENGTH,
} from '@app/main/leak-detection/dialogs/dialog-event/configs/dialog-event-comment.config';

/** what the editor changes: a status transition, or — status left as it is — the event's own result */
type DialogEventChange = Pick<
  IDialogEventStatusTransition,
  'target' | 'investigationResult' | 'form' | 'comment' | 'commentWording' | 'confirmLabel'
>;

/** what discarding the event's investigation result leaves of it */
const CLEARED_RESOLUTION: Partial<IDialogEventDraft> = {
  investigationResult: null,
  repairStatus: null,
  repairDate: null,
  leakSize: null,
  pressure: null,
  soilComposition: null,
  nonLeakNoiseSource: null,
  nonLeakOtherDetails: null,
};

/** the Result editor's title, by the result it edits */
const RESULT_EDIT_TITLE: Record<LdEventInvestigationResult, string> = {
  [LdEventInvestigationResult.Leak]: _(`@@fp-event-result-edit__title`),
  [LdEventInvestigationResult.NoLeak]: _(`@@fp-event-result-edit__title-no-leak`),
  [LdEventInvestigationResult.CustomerSideLeak]: _(`@@fp-event-result-edit__title-customer-side-leak`),
};

// the Status and Result editors: a change of status, or an edit of the result the event has
@Component({
  selector: 'gc-dialog-event-change-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateDirective,
    TranslatePipe,
    Button,
    Select,
    Textarea,
    RadioButton,
    SvgIconComponent,
    DialogEventBadgesComponent,
    DialogEventResolutionFieldsComponent,
  ],
  templateUrl: './dialog-event-change-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col h-full min-h-0' },
})
export class DialogEventChangeFormComponent {
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tabs = inject(DialogEventTabsService);
  private readonly assigneeService = inject(LeakDetectionEventsAssigneeService);
  private readonly translateService = inject(TranslateService);

  protected readonly dialogState = inject(DialogEventState);

  /** keeps the dropdown panel above the floating popup — see the config's note */
  protected readonly overlayOptions = DIALOG_EVENT_OVERLAY_OPTIONS;

  protected readonly maxCommentLength = DIALOG_EVENT_MAX_COMMENT_LENGTH;

  protected readonly FormKind = DialogEventStatusActionForm;
  protected readonly CommentRequirement = DialogEventCommentRequirement;

  /** the draft the change starts from */
  private readonly base = this.dialogState.draft();

  /** the Result editor opens from the result card, so there is a result to edit */
  protected readonly isResultEdit = this.tabs.editor() === DialogEventEditor.Result;

  protected readonly change: DialogEventChange = this.isResultEdit
    ? {
        target: this.base.status,
        investigationResult: this.base.investigationResult,
        form: DialogEventStatusActionForm.Resolution,
        comment: DialogEventCommentRequirement.Optional,
        confirmLabel: _(`@@shared-save`),
      }
    : // `openStatusChange` sets the action together with this editor
      dialogEventStatusTransitions[this.tabs.statusAction()!];

  protected readonly resultEditTitle =
    RESULT_EDIT_TITLE[this.change.investigationResult ?? LdEventInvestigationResult.Leak];

  protected readonly commentWording = this.change.commentWording ?? DIALOG_EVENT_COMMENT_WORDING;

  /** nobody on it is allowed only while New or Monitoring — the same rule as the Details tab's */
  protected readonly isAssigneeRequired = needsAssignee(this.change.target);

  protected readonly existingResultLabel =
    this.change.investigationResult === null && this.base.investigationResult
      ? dialogEventResultLabel(this.base.investigationResult, this.dialogState.repairStatus())
      : null;

  protected readonly keepResultOptions = [
    { value: true, label: _(`@@fp-event-status-form__keep-result`) },
    { value: false, label: _(`@@fp-event-status-form__discard-result`) },
  ];

  protected readonly form = this.fb.group<IDialogEventChangeForm>({
    assignedTo: this.fb.control(
      this.base.assignedTo,
      this.change.form === DialogEventStatusActionForm.Assignee && this.isAssigneeRequired ? Validators.required : null
    ),
    keepResult: this.fb.nonNullable.control(true),
    comment: this.fb.nonNullable.control('', [
      ...(this.change.comment === DialogEventCommentRequirement.Required
        ? [Validators.required, trimmedMinLength(1)]
        : []),
      Validators.maxLength(DIALOG_EVENT_MAX_COMMENT_LENGTH),
    ]),
    ...(this.change.investigationResult
      ? { resolution: this.createResolutionForm(this.change.investigationResult) }
      : {}),
  });

  private readonly initialValue = this.form.getRawValue();

  private readonly assignableUsers = toSignal(this.assigneeService.assignableUsers$, {
    initialValue: [] as string[],
  });

  protected readonly assigneeOptions = computed(() => {
    const users = this.assignableUsers().map(user => ({ label: user, value: user }));

    return this.isAssigneeRequired
      ? users
      : [{ label: this.translateService.instant(_(`@@shared-events-not-assigned__text`)), value: null }, ...users];
  });

  protected hasError(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }

  protected hasRequiredFields(control: AbstractControl = this.form): boolean {
    if (control.disabled) return false;

    if (control instanceof FormGroup)
      return Object.values(control.controls).some(child => this.hasRequiredFields(child));

    return control.hasValidator(Validators.required);
  }

  protected isUnchanged(): boolean {
    return this.change.target === this.base.status && equal(this.form.getRawValue(), this.initialValue);
  }

  protected pendingRepairStatus(): LdEventRepairStatus | null {
    return isLeakFoundResult(this.change.investigationResult)
      ? (this.form.controls.resolution?.controls.repairStatus.value ?? null)
      : null;
  }

  protected cancel(): void {
    this.tabs.closeEditor();
  }

  protected confirm(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();

      return;
    }

    const { assignedTo, keepResult, comment } = this.form.getRawValue();
    const { resolution } = this.form.controls;

    const next: IDialogEventDraft = {
      ...this.base,
      ...(keepResult ? {} : CLEARED_RESOLUTION),
      status: this.change.target,
      assignedTo,
      ...(resolution
        ? { investigationResult: this.change.investigationResult, ...this.resolutionPatch(resolution) }
        : {}),
    };

    this.dialogState
      .saveDraft(next, comment)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(saved => {
        if (saved) this.tabs.closeEditor();
      });
  }

  private createResolutionForm(result: LdEventInvestigationResult): DialogEventResolutionForm {
    const isNoLeak = result === LdEventInvestigationResult.NoLeak;
    const hasRepair = isLeakFoundResult(result);

    const { repairStatus, repairDate } =
      hasRepair && !this.isResultEdit ? { repairStatus: LdEventRepairStatus.NotRepaired, repairDate: null } : this.base;
    const { fb, base } = this;

    return fb.group<IDialogEventResolutionForm>({
      leakSize: fb.nonNullable.control<number | null>({ value: base.leakSize, disabled: isNoLeak }, positiveNumber),
      leakSizeUnit: fb.nonNullable.control(base.leakSizeUnit),
      nonLeakNoiseSource: fb.nonNullable.control<NonLeakNoiseSource | null>({
        value: base.nonLeakNoiseSource,
        disabled: !isNoLeak,
      }),
      nonLeakOtherDetails: fb.nonNullable.control<string | null>({
        value: base.nonLeakOtherDetails,
        disabled: !isNoLeak,
      }),
      pressure: fb.nonNullable.control(pressureDisplayValue(base), positiveNumber),
      pressureUnit: fb.nonNullable.control(base.pressureUnit),
      soilComposition: fb.nonNullable.control(base.soilComposition),
      repairStatus: fb.nonNullable.control<LdEventRepairStatus | null>({ value: repairStatus, disabled: !hasRepair }),
      // required whenever it is asked for: it is enabled only while the repair status has a date
      repairDate: fb.nonNullable.control<Date | null>(
        {
          value: repairDate ? dayjs(repairDate).toDate() : null,
          disabled: !hasRepair || !this.hasRepairDate(repairStatus),
        },
        [Validators.required, dialogEventRepairDateValidator]
      ),
    });
  }

  private resolutionPatch(form: DialogEventResolutionForm): Partial<IDialogEventDraft> {
    const { repairDate, pressure, pressureUnit, nonLeakOtherDetails, ...resolution } = form.getRawValue();
    const { controls } = form;

    const isPressureEdited = controls.pressure.dirty || controls.pressureUnit.dirty;

    return {
      ...resolution,
      nonLeakOtherDetails: resolution.nonLeakNoiseSource === NonLeakNoiseSource.Other ? nonLeakOtherDetails : null,
      pressureUnit,
      pressure: isPressureEdited ? this.toStoredPressure(pressure, pressureUnit) : this.base.pressure,
      repairDate: repairDate ? toDateStr(dayjs(repairDate)) : null,
    };
  }

  private toStoredPressure(value: number | null, unit: PressureUnit): number | null {
    return value === null ? null : new Pressure(value, unit).to(DEFAULT_PRESSURE_UNIT);
  }

  private hasRepairDate(repairStatus: LdEventRepairStatus | null): boolean {
    return repairStatus === LdEventRepairStatus.RepairScheduled || repairStatus === LdEventRepairStatus.Repaired;
  }
}
